<template>
  <div class="coordinator-dashboard pa-10">
    <!-- ── Header ── -->
    <div class="d-flex flex-wrap justify-space-between align-center mb-7 gap-3">
      <div>
        <div class="dash-page-title">{{ dashboardTitle }}</div>
        <div class="dash-page-sub mt-1">{{ dashboardSubtitle }}</div>
      </div>
      <v-btn
        color="#10b981"
        variant="flat"
        prepend-icon="mdi-download-outline"
        class="text-white"
        rounded="lg"
        size="large"
        @click="showReportsDialog = true"
      >
        Generate Reports
      </v-btn>
    </div>

    <GenerateReportsDialog
      v-if="showReportsDialog"
      @close="showReportsDialog = false"
    />

    <!-- ── Stat Cards ── -->
    <v-row class="mb-5" style="gap: 0">
      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--amber rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--amber rounded-lg pa-2">
              <v-icon size="22" color="#d97706">mdi-clock-outline</v-icon>
            </div>
            <v-icon size="18" color="#d97706">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--amber">
            {{ stats.for_matching }}
          </div>
          <div class="stat-label stat-label--amber">For Matching</div>
          <div class="stat-sub">Awaiting job matching</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--purple rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--purple rounded-lg pa-2">
              <v-icon size="22" color="#7c3aed">mdi-send-outline</v-icon>
            </div>
            <v-icon size="18" color="#7c3aed">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--purple">
            {{ stats.endorsed }}
          </div>
          <div class="stat-label stat-label--purple">Endorsed</div>
          <div class="stat-sub">Pending HTE acceptance</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--green rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--green rounded-lg pa-2">
              <v-icon size="22" color="#059669"
                >mdi-account-check-outline</v-icon
              >
            </div>
            <v-icon size="18" color="#059669">mdi-check-circle-outline</v-icon>
          </div>
          <div class="stat-number stat-number--green">{{ stats.deployed }}</div>
          <div class="stat-label stat-label--green">Deployed</div>
          <div class="stat-sub">Currently in training</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--blue rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--blue rounded-lg pa-2">
              <v-icon size="22" color="#2563eb"
                >mdi-office-building-outline</v-icon
              >
            </div>
            <v-icon size="18" color="#2563eb">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--blue">
            {{ stats.partner_htes }}
          </div>
          <div class="stat-label stat-label--blue">Partner HTEs</div>
          <div class="stat-sub">Active partnerships</div>
        </div>
      </v-col>
    </v-row>

    <!-- ── Charts Row 1 ── -->
    <v-row class="mb-5">
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Student Deployment Overview</div>
            <v-icon size="20" color="#94a3b8">mdi-account-group-outline</v-icon>
          </div>
          <div class="card-sub mb-5">Current matching and placement status</div>
          <div class="chart-wrap chart-wrap--doughnut">
            <canvas ref="deploymentChart"></canvas>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Cumulative Training Hours</div>
            <v-icon size="20" color="#94a3b8">mdi-clock-outline</v-icon>
          </div>
          <div class="card-sub mb-5">
            Total hours logged across all students
          </div>
          <div class="chart-wrap">
            <canvas ref="trainingHoursChart"></canvas>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- ── Charts Row 2 ── -->
    <v-row class="mb-5">
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Requirements Submission</div>
            <v-icon size="20" color="#94a3b8">mdi-file-document-outline</v-icon>
          </div>
          <div class="card-sub mb-5">Document completion by program</div>
          <div class="chart-wrap">
            <canvas ref="requirementsChart"></canvas>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Average Evaluation Scores</div>
            <v-icon size="20" color="#94a3b8">mdi-star-outline</v-icon>
          </div>
          <div class="card-sub mb-5">
            Performance ratings from industry supervisors
          </div>
          <div class="chart-wrap">
            <canvas ref="evaluationChart"></canvas>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- ── Quick Report Generation ── -->
    <div class="dash-card rounded-xl pa-6 mb-5">
      <div class="d-flex justify-space-between align-center mb-5">
        <div class="card-title">Quick Report Generation</div>
        <a class="view-all-link">View All Reports →</a>
      </div>
      <v-row>
        <v-col cols="12" sm="4">
          <div class="report-card report-card--active rounded-xl pa-5">
            <v-icon color="#059669" size="26" class="mb-3"
              >mdi-account-multiple-outline</v-icon
            >
            <div class="report-title">Student Practicum Profile</div>
            <div class="report-sub">Master list of all OJT students</div>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="report-card rounded-xl pa-5">
            <v-icon color="#6b7280" size="26" class="mb-3"
              >mdi-clock-outline</v-icon
            >
            <div class="report-title">OJT Hours Monitoring</div>
            <div class="report-sub">Track student training hours</div>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="report-card rounded-xl pa-5">
            <v-icon color="#6b7280" size="26" class="mb-3"
              >mdi-office-building-outline</v-icon
            >
            <div class="report-title">HTE Directory</div>
            <div class="report-sub">Partner companies database</div>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- ── Critical Alerts ── -->
    <div class="dash-card rounded-xl pa-6">
      <div class="d-flex align-center gap-4 mb-5">
        <v-icon color="#f59e0b" size="20">mdi-alert-outline</v-icon>&nbsp;
        <div class="card-title">Critical Alerts &amp; Action Items</div>
      </div>

      <div
        class="alert-item rounded-xl pa-4 mb-3"
        style="background: #fff7ed; border: 1px solid #fed7aa"
      >
        <div class="d-flex align-start" style="gap: 16px">
          <div
            class="alert-icon-box rounded-lg pa-2"
            style="background: #fde8cc"
          >
            <v-icon color="#ea580c" size="20">mdi-calendar-alert</v-icon>
          </div>
          <div>
            <div class="alert-title">
              {{ alerts.expiring_moas }} MOAs Expiring Soon
            </div>
            <div class="alert-desc">
              Partnership agreements require renewal within 30 days
            </div>
            <a class="alert-link" style="color: #ea580c">View Details</a>
          </div>
        </div>
      </div>

      <div
        class="alert-item rounded-xl pa-4 mb-3"
        style="background: #fefce8; border: 1px solid #fef08a"
      >
        <div class="d-flex align-start" style="gap: 16px">
          <div
            class="alert-icon-box rounded-lg pa-2"
            style="background: #fef3c7"
          >
            <v-icon color="#ca8a04" size="20"
              >mdi-account-multiple-outline</v-icon
            >
          </div>
          <div>
            <div class="alert-title">
              {{ alerts.awaiting_matching }} Students Awaiting Matching
            </div>
            <div class="alert-desc">
              Students need to be matched with partner companies
            </div>
            <a class="alert-link" style="color: #ca8a04">Go to Matching</a>
          </div>
        </div>
      </div>

      <div
        class="alert-item rounded-xl pa-4"
        style="background: #eff6ff; border: 1px solid #bfdbfe"
      >
        <div class="d-flex align-start" style="gap: 16px">
          <div
            class="alert-icon-box rounded-lg pa-2"
            style="background: #dbeafe"
          >
            <v-icon color="#2563eb" size="20">mdi-file-document-outline</v-icon>
          </div>
          <div>
            <div class="alert-title">
              {{ alerts.pending_documents }} Documents Pending Review
            </div>
            <div class="alert-desc">
              Student requirements awaiting validation
            </div>
            <a class="alert-link" style="color: #2563eb">Review Documents</a>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Recent Activity ── -->
    <div class="dash-card rounded-xl pa-6 mt-5">
      <div class="d-flex justify-space-between align-center mb-4">
        <div>
          <div class="card-title">Recent Activity</div>
          <div class="card-sub">Latest actions in your program</div>
        </div>
        <router-link
          :to="`/${currentUserPath}/activity-log`"
          class="view-all-link"
        >
          View All →
        </router-link>
      </div>

      <div v-if="activityLoading" class="d-flex justify-center py-6">
        <v-progress-circular indeterminate color="#10b981" size="28" />
      </div>

      <div v-else-if="!recentActivity.length" class="text-center py-6">
        <div style="color: #9ca3af; font-size: 13px">No recent activity</div>
      </div>

      <div v-else>
        <div
          v-for="log in recentActivity"
          :key="log.id"
          class="d-flex align-start py-3"
          style="border-bottom: 1px solid #f3f4f6"
        >
          <div
            class="rounded-lg d-flex align-center justify-center mr-3 flex-shrink-0"
            :style="getActivityBadgeStyle(log.action)"
            style="width: 34px; height: 34px; margin-top: 1px"
          >
            <v-icon size="15" :color="getActivityColor(log.action)">{{
              getActivityIcon(log.action)
            }}</v-icon>
          </div>
          <div class="flex-grow-1">
            <div
              class="d-flex justify-space-between align-start"
              style="gap: 8px"
            >
              <span style="font-size: 13px; font-weight: 500; color: #111827">{{
                log.description || log.action
              }}</span>
              <span
                style="
                  font-size: 11px;
                  color: #9ca3af;
                  white-space: nowrap;
                  flex-shrink: 0;
                "
                >{{ formatActivityTime(log.created_at) }}</span
              >
            </div>
            <div style="font-size: 11.5px; color: #9ca3af; margin-top: 2px">
              {{ log.performed_by_name || "" }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import GenerateReportsDialog from "@/components/Dialogs/Forms/GenerateReportsDialog.vue";
export default {
  name: "DashboardPage",
  components: { GenerateReportsDialog },
  data() {
    return {
      showReportsDialog: false,
      charts: {},
      loading: false,
      stats: {
        for_matching: 0,
        endorsed: 0,
        deployed: 0,
        partner_htes: 0,
      },
      alerts: {
        expiring_moas: 0,
        awaiting_matching: 0,
        pending_documents: 0,
      },
      deploymentChartData: [],
      trainingHoursData: [],
      requirementsData: [],
      evaluationData: [],
      recentActivity: [],
      activityLoading: false,
      is_active: true,
    };
  },
  computed: {
    activeRole() {
      return this.$store.getters.getActiveRole;
    },
    dashboardTitle() {
      return this.activeRole == 1
        ? "Coordinator Dashboard"
        : "Instructor Dashboard";
    },
    dashboardSubtitle() {
      return this.activeRole == 1
        ? "OJT Program Management & Student Deployment"
        : "Monitor Intern Progress & Document Validation";
    },
    currentUserPath() {
      return this.activeRole == 1 ? "ojt-coordinator" : "instructor";
    },
  },
  mounted() {
    this.fetchDashboardData();
    this.fetchRecentActivity();
  },
  beforeUnmount() {
    this.is_active = false;
    Object.values(this.charts).forEach((c) => {
      if (c) {
        c.stop();
        c.destroy();
      }
    });
  },
  methods: {
    async fetchDashboardData() {
      let shouldInitCharts = true;

      this.loading = true;
      try {
        const res = await this.axiosCall(
          "/student/coordinator-dashboard",
          "GET",
        );
        if (!this.is_active) {
          shouldInitCharts = false;
        } else if (res?.data?.stats) {
          const d = res.data;
          this.stats = d.stats;
          this.alerts = d.alerts ?? this.alerts;
          this.deploymentChartData = d.deployment_chart ?? [];
          this.trainingHoursData = d.training_hours_chart ?? [];
          this.requirementsData = d.requirements_chart ?? [];
          this.evaluationData = d.evaluation_scores ?? [];
        }
      } catch (e) {
        console.error("Failed to load coordinator dashboard", e);
        shouldInitCharts = false;
      } finally {
        this.loading = false;
      }

      if (!shouldInitCharts || !this.is_active) return;

      this.$nextTick(() => {
        if (!this.is_active) return;
        this.initDeploymentChart();
        this.initTrainingHoursChart();
        this.initRequirementsChart();
        this.initEvaluationChart();
      });
    },

    initDeploymentChart() {
      if (!this.$refs.deploymentChart) return;
      if (this.charts.deployment) this.charts.deployment.destroy();
      const labels = this.deploymentChartData.map((d) => d.status);
      const data = this.deploymentChartData.map((d) => d.count);
      this.charts.deployment = new Chart(this.$refs.deploymentChart, {
        type: "doughnut",
        data: {
          labels: labels.length
            ? labels
            : ["For Matching", "Endorsed", "Deployed"],
          datasets: [
            {
              data: data.length ? data : [0, 0, 0],
              backgroundColor: ["#fbbf24", "#8b5cf6", "#10b981"],
              borderWidth: 3,
              borderColor: "#fff",
              hoverOffset: 6,
            },
          ],
        },
        options: {
          clip: false,
          cutout: "72%",
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 800 },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                usePointStyle: true,
                pointStyle: "circle",
                padding: 20,
                font: { size: 12 },
                color: "#6b7280",
              },
            },
          },
        },
      });
    },

    initTrainingHoursChart() {
      if (!this.$refs.trainingHoursChart) return;
      if (this.charts.trainingHours) this.charts.trainingHours.destroy();
      const labels = this.trainingHoursData.map((d) => d.month);
      const data = this.trainingHoursData.map((d) => d.total_hours);
      this.charts.trainingHours = new Chart(this.$refs.trainingHoursChart, {
        type: "line",
        data: {
          labels: labels.length ? labels : [],
          datasets: [
            {
              label: "Training Hours",
              data: data.length ? data : [],
              borderColor: "#10b981",
              backgroundColor: "rgba(16,185,129,0.08)",
              pointBackgroundColor: "#10b981",
              pointRadius: 5,
              fill: "origin",
              clip: false,
              tension: 0.35,
              borderWidth: 2.5,
            },
          ],
        },
        options: {
          clip: false,
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 800 },
          plugins: { legend: { display: false } },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: "#9ca3af", font: { size: 11 } },
              border: { display: false },
            },
            y: {
              beginAtZero: true,
              grid: { color: "#f3f4f6" },
              ticks: {
                color: "#9ca3af",
                font: { size: 11 },
                callback: (v) => (v >= 1000 ? v / 1000 + "k" : v),
              },
              border: { display: false },
            },
          },
        },
      });
    },

    initRequirementsChart() {
      if (!this.$refs.requirementsChart) return;
      if (this.charts.requirements) this.charts.requirements.destroy();
      const labels = this.requirementsData.map((d) => d.program);
      const submitted = this.requirementsData.map((d) => d.submitted);
      const pending = this.requirementsData.map((d) => d.pending);
      this.charts.requirements = new Chart(this.$refs.requirementsChart, {
        type: "bar",
        data: {
          labels: labels.length ? labels : [],
          datasets: [
            {
              label: "Validated",
              data: submitted.length ? submitted : [],
              backgroundColor: "#10b981",
              borderRadius: 4,
              barPercentage: 0.45,
            },
            {
              label: "Pending Review",
              data: pending.length ? pending : [],
              backgroundColor: "#e5e7eb",
              borderRadius: 4,
              barPercentage: 0.45,
            },
          ],
        },
        options: {
          clip: false,
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 800 },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                usePointStyle: true,
                pointStyle: "circle",
                padding: 16,
                font: { size: 11 },
                color: "#6b7280",
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: "#9ca3af", font: { size: 11 } },
              border: { display: false },
            },
            y: {
              beginAtZero: true,
              grid: { color: "#f3f4f6" },
              ticks: { color: "#9ca3af", font: { size: 11 } },
              border: { display: false },
            },
          },
        },
      });
    },

    initEvaluationChart() {
      if (!this.$refs.evaluationChart) return;
      if (this.charts.evaluation) this.charts.evaluation.destroy();
      const labels = this.evaluationData.map((d) => d.program);
      const scores = this.evaluationData.map((d) => d.avg_score);
      this.charts.evaluation = new Chart(this.$refs.evaluationChart, {
        type: "bar",
        data: {
          labels: labels.length ? labels : [],
          datasets: [
            {
              label: "Avg Score",
              data: scores.length ? scores : [],
              backgroundColor: "#f59e0b",
              borderRadius: 4,
              barPercentage: 0.5,
            },
          ],
        },
        options: {
          clip: false,
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 800 },
          plugins: { legend: { display: false } },
          scales: {
            x: {
              beginAtZero: true,
              max: 5,
              grid: { color: "#f3f4f6" },
              ticks: { color: "#9ca3af", font: { size: 11 } },
              border: { display: false },
            },
            y: {
              grid: { display: false },
              ticks: { color: "#374151", font: { size: 11 } },
              border: { display: false },
            },
          },
        },
      });
    },

    async fetchRecentActivity() {
      this.activityLoading = true;
      try {
        const res = await this.axiosCall("/system-logs?limit=5&page=1", "GET");
        if (res?.data?.data) {
          this.recentActivity = res.data.data;
        }
      } catch (e) {
        console.error("Failed to load recent activity", e);
      } finally {
        this.activityLoading = false;
      }
    },

    getActivityConfig(action) {
      const map = {
        USER_LOGIN: {
          color: "#059669",
          bg: "#ecfdf5",
          border: "#a7f3d0",
          icon: "mdi-login-variant",
        },
        USER_LOGOUT: {
          color: "#6b7280",
          bg: "#f9fafb",
          border: "#e5e7eb",
          icon: "mdi-logout-variant",
        },
        BATCH_CREATED: {
          color: "#059669",
          bg: "#ecfdf5",
          border: "#a7f3d0",
          icon: "mdi-layers-plus",
        },
        STUDENT_ENDORSED: {
          color: "#7c3aed",
          bg: "#f5f3ff",
          border: "#ddd6fe",
          icon: "mdi-send-outline",
        },
        STUDENT_DEPLOYED: {
          color: "#2563eb",
          bg: "#eff6ff",
          border: "#bfdbfe",
          icon: "mdi-check-circle-outline",
        },
        STUDENT_PULLED_OUT: {
          color: "#dc2626",
          bg: "#fef2f2",
          border: "#fecaca",
          icon: "mdi-account-arrow-left-outline",
        },
        STUDENT_REASSIGNED: {
          color: "#d97706",
          bg: "#fffbeb",
          border: "#fde68a",
          icon: "mdi-account-switch-outline",
        },
        REQUIREMENT_SUBMITTED: {
          color: "#2563eb",
          bg: "#eff6ff",
          border: "#bfdbfe",
          icon: "mdi-file-upload-outline",
        },
        REQUIREMENT_APPROVED: {
          color: "#059669",
          bg: "#ecfdf5",
          border: "#a7f3d0",
          icon: "mdi-file-check-outline",
        },
        REQUIREMENT_DISAPPROVED: {
          color: "#dc2626",
          bg: "#fef2f2",
          border: "#fecaca",
          icon: "mdi-file-cancel-outline",
        },
        ATTENDANCE_SCANNED: {
          color: "#0d9488",
          bg: "#f0fdfa",
          border: "#99f6e4",
          icon: "mdi-qrcode-scan",
        },
      };
      return (
        map[action] ?? {
          color: "#6b7280",
          bg: "#f9fafb",
          border: "#e5e7eb",
          icon: "mdi-history",
        }
      );
    },
    getActivityColor(action) {
      return this.getActivityConfig(action).color;
    },
    getActivityIcon(action) {
      return this.getActivityConfig(action).icon;
    },
    getActivityBadgeStyle(action) {
      const c = this.getActivityConfig(action);
      return `background:${c.bg}; border:1px solid ${c.border};`;
    },

    formatActivityTime(val) {
      if (!val) return "";
      const d = new Date(val);
      return d.toLocaleString("en-PH", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.coordinator-dashboard {
  min-height: 100vh;
}

/* ── Header ── */
.dash-page-title {
  font-size: 30px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.dash-page-sub {
  font-size: 14px;
  color: #6b7280;
}

/* ── Stat Cards ── */
.stat-card {
  border: 1px solid transparent;
  transition: box-shadow 0.2s;
  height: 100%;
}
.stat-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.09);
}
.stat-card--amber {
  background: #fffbeb;
  border-color: #fde68a;
}
.stat-card--purple {
  background: #f5f3ff;
  border-color: #ddd6fe;
}
.stat-card--green {
  background: #ecfdf5;
  border-color: #a7f3d0;
}
.stat-card--blue {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.stat-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.stat-icon-box--amber {
  background: #fde68a;
}
.stat-icon-box--purple {
  background: #ddd6fe;
}
.stat-icon-box--green {
  background: #a7f3d0;
}
.stat-icon-box--blue {
  background: #bfdbfe;
}

.stat-number {
  font-size: 40px;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -1.5px;
}
.stat-number--amber {
  color: #d97706;
}
.stat-number--purple {
  color: #7c3aed;
}
.stat-number--green {
  color: #059669;
}
.stat-number--blue {
  color: #2563eb;
}

.stat-label {
  font-size: 13px;
  font-weight: 600;
  margin-top: 6px;
}
.stat-label--amber {
  color: #d97706;
}
.stat-label--purple {
  color: #7c3aed;
}
.stat-label--green {
  color: #059669;
}
.stat-label--blue {
  color: #2563eb;
}

.stat-sub {
  font-size: 11.5px;
  color: #9ca3af;
  margin-top: 3px;
}

/* ── Dash Cards ── */
.dash-card {
  background: #ffffff;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}

.card-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.card-sub {
  font-size: 12.5px;
  color: #9ca3af;
}

/* ── Charts ── */
.chart-wrap {
  position: relative;
  width: 100%;
  height: 230px;
}

.chart-wrap--doughnut {
  height: 210px;
}

/* ── Quick Reports ── */
.view-all-link {
  font-size: 13px;
  color: #10b981;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
}
.view-all-link:hover {
  text-decoration: underline;
}

.report-card {
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
  height: 100%;
}
.report-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07);
  border-color: #d1fae5;
}
.report-card--active {
  background: #f0fdf4;
  border-color: #a7f3d0;
}

.report-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 3px;
}

.report-sub {
  font-size: 12px;
  color: #9ca3af;
}

/* ── Alerts ── */
.alert-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.alert-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 2px;
}

.alert-desc {
  font-size: 12.5px;
  color: #6b7280;
  margin-bottom: 6px;
}

.alert-link {
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}
.alert-link:hover {
  text-decoration: underline;
}
</style>
